import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Login from './Login';
import Signup from './Signup';
import ForgotPassword from './ForgotPassword';
import Header from './Header';
import Footer from './Footer';

const Auth = ({ onAuthSuccess, onClose, onHomeClick, onCategoriesClick, onHowItWorksClick, defaultMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(defaultMode === 'login');
  const [expirationMessage, setExpirationMessage] = useState(null);
  const [authMode, setAuthMode] = useState(defaultMode === 'login' ? 'login' : 'signup'); // 'login', 'signup', 'forgot-password'
  const location = useLocation();
  const navigate = useNavigate();

  // Update form mode based on route
  useEffect(() => {
    console.log('Auth component defaultMode changed:', defaultMode);
    setIsLogin(defaultMode === 'login');
    setAuthMode(defaultMode === 'login' ? 'login' : 'signup');
  }, [defaultMode]);


  // Check for messages from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setExpirationMessage(location.state.message);
      // Clear the message after 5 seconds
      setTimeout(() => {
        setExpirationMessage(null);
      }, 5000);
    }
  }, [location.state]);

  const handleLogin = (userData) => {
    console.log('Login successful:', userData);
    // Here you would typically store the user data in context/state
    onAuthSuccess(userData, 'login');
  };

  const handleSignup = (userData) => {
    console.log('Signup successful:', userData);
    // Here you would typically store the user data in context/state
    onAuthSuccess(userData, 'signup');
  };

  const switchToSignup = () => {
    navigate('/signup');
    setAuthMode('signup');
  };

  const switchToLogin = () => {
    navigate('/login');
    setAuthMode('login');
  };

  const switchToForgotPassword = () => {
    setAuthMode('forgot-password');
  };

  const backToLogin = () => {
    setAuthMode('login');
  };

  const handleLoginClick = () => {
    // Navigate to login if not already there
    if (location.pathname !== '/login') {
      navigate('/login');
    }
  };

  const handleSignupClick = () => {
    // Navigate to signup if not already there
    if (location.pathname !== '/signup') {
      navigate('/signup');
    }
  };


  const handleBackToHome = () => {
    if (onClose) {
      onClose();
    }
  };

  // Add class to body on mobile for styling
  React.useEffect(() => {
    if (window.innerWidth <= 768) {
      document.body.classList.add('auth-page-active');
      return () => {
        document.body.classList.remove('auth-page-active');
      };
    }
  }, []);

  return (
    <div className="auth-page">
      {/* Header - visible on desktop/tablet only */}
      <div className="auth-header-desktop">
        <Header 
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          isLoggedIn={false}
          user={null}
          onBackClick={handleBackToHome}
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          currentPage={location.pathname === '/login' ? 'login' : 'signup'}
        />
      </div>
      
      {/* Mobile Back Button */}
      <div className="auth-back-button-mobile">
        <button 
          className="auth-back-btn"
          onClick={handleBackToHome}
          aria-label="Go back"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12,19 5,12 12,5"></polyline>
          </svg>
          <span>Back</span>
        </button>
      </div>
      
      {/* Success/Warning Message */}
      {expirationMessage && (
        <div className={`auth-message-alert ${location.state?.type === 'success' ? 'success' : 'warning'}`}>
          <div className="alert-content">
            <div className="alert-icon">
              {location.state?.type === 'success' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              )}
            </div>
            <div className="alert-message">
              <strong>{location.state?.type === 'success' ? 'Welcome to Your Personal Career Copilot!' : 'Session Expired'}</strong>
              <p>{expirationMessage}</p>
            </div>
            <button 
              className="alert-close"
              onClick={() => setExpirationMessage(null)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <div className="auth-hero-section">
        <div className="auth-hero-content">
          {/* Left side with benefits - hidden on mobile */}
          <div className="auth-social-section auth-social-desktop">
            <div className="auth-hero-text">
              <h1>{isLogin ? 'Welcome Back!' : 'Start Your Career Journey'}</h1>
              <p>
                {isLogin 
                  ? 'Sign in to access your career dashboard, manage your profile, discover opportunities, and connect with top talent.' 
                  : 'Join Your Personal Career Copilot. Discover opportunities, showcase your skills, connect with employers, and advance your career.'
                }
              </p>
              
              {isLogin && (
                <div className="login-benefits-list">
                  <div className="benefit-item">
                    <span className="benefit-icon">📊</span>
                    <span>Access your personalized career dashboard</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">💼</span>
                    <span>Discover job opportunities tailored to you</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">👥</span>
                    <span>Connect with employers and top talent</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">🔔</span>
                    <span>Get notified about new opportunities</span>
                  </div>
                </div>
              )}
              
              {!isLogin && (
                <div className="benefits-list">
                  <div className="benefit-item">
                    <span className="benefit-icon">✓</span>
                    <span>Free to join - Start your career journey today</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">✓</span>
                    <span>Secure platform with verified profiles</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">✓</span>
                    <span>Connect with employers and professionals worldwide</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">✓</span>
                    <span>Showcase your skills and grow your career</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Form Section - full width on mobile */}
          <div className="auth-form-section">
            {authMode === 'login' && (
              <Login 
                onSwitchToSignup={switchToSignup}
                onLogin={handleLogin}
                onForgotPassword={switchToForgotPassword}
              />
            )}
            {authMode === 'signup' && (
              <Signup 
                onSwitchToLogin={switchToLogin}
                onSignup={handleSignup}
              />
            )}
            {authMode === 'forgot-password' && (
              <ForgotPassword 
                onBackToLogin={backToLogin}
              />
            )}
          </div>
        </div>
      </div>

      {/* Footer - visible on desktop/tablet only */}
      <div className="auth-footer-desktop">
        <Footer />
      </div>
    </div>
  );
};

export default Auth;
